import { Component, inject } from '@angular/core';
import { ChromadbService } from '../../services/chromadb.service';
import { NgFor, NgIf, CommonModule } from '@angular/common';
import { FormsModule, NgModel } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router'; 

interface Collection {
  documents:Document[]
}

interface Document {
  text: string
  id: number
  metadata: metadata
  dropdown: boolean
  info:boolean
}

interface metadata {
  bias_ai: number | string;
  category: string;
  confidence: number | string;
  counter: number;
  datetime: string;
  relevance_ai: number;
  relevance_caregiver: string;
  relevant_datetime: string;
  sensitivity: number;
  sentiment: string;
  session_uuid: string;
  tags: string;
  timestamp: number | string;
  title: string;
  topics: string;
  summary?: string;
  [key: string]: any;
}

export const CATEGORY_OPTIONS: string[] = [
  "PAST_EVENT",
  "PRESENT_EVENT",
  "FUTURE_EVENT",
  "ROUTINE",
  "PERSONAL_DATA",
  "OTHER"
];

export const RELEVANCE_CAREGIVER_OPTIONS: string[] = [
  "HIGH",
  "MEDIUM",
  "LOW"
];

export const TOPIC_OPTIONS: string[] = [
  "Physical Health & Well-being",
  "Emotional & Mental Health",
  "Cognitive Function",
  "Relationships & Bonds",
  "Routine & Daily Life",
  "Leisure & Culture & Hobbies",
  "Identity & Biography",
  "Technology & the Digital World",
  "Work & Economy",
  "Values & Philosophy",
  "Learning & Science",
  "Environment & Location",
  "Feedback & Support",
  "Other"
];

export const SENTIMENT_OPTIONS: string[] = [
  "JOY",
  "FRUSTRATION",
  "ANXIETY",
  "NEUTRAL"
];

export const SENSITIVITY_OPTIONS: number[] = [0, 1, 2, 3, 4];

function getDefaultMetadata(): metadata {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const formattedNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  let uuid = '';
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    uuid = crypto.randomUUID();
  }

  return {
    bias_ai: 0,
    category: 'PRESENT_EVENT',
    confidence: 1,
    counter: 0,
    datetime: formattedNow,
    relevance_ai: 0,
    relevance_caregiver: 'LOW',
    relevant_datetime: formattedNow,
    sensitivity: 0,
    sentiment: 'NEUTRAL',
    session_uuid: uuid,
    tags: '',
    timestamp: Date.now(),
    title: '',
    topics: '',
    summary: ''
  };
}

export interface modal {
  title: string;
  document: Document;
  visible: boolean;
}

export interface edit extends modal{
  newData: string;
}

export interface create extends modal{
  name: string;
  distance: string;
  embedding_function: string;
}

@Component({
  selector: 'app-collection',
  imports: [NgFor,NgIf,CommonModule,FormsModule],
  templateUrl: './collection.component.html',
  styleUrl: './collection.component.css'
})
export class CollectionComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public collectionName: string = '';
  public collectionDocs: number = 0;

  // private urlItems = window.location.pathname.split("/")
  // public collectionName = this.urlItems[this.urlItems.length-1];

  public collection:Collection = {
    documents:[]
  }
  public searchQuery = ""
  public searchLimit = 10

  // Constantes de opciones para la vista
  readonly categoryOptions = CATEGORY_OPTIONS;
  readonly relevanceCaregiverOptions = RELEVANCE_CAREGIVER_OPTIONS;
  readonly topicOptions = TOPIC_OPTIONS;
  readonly sentimentOptions = SENTIMENT_OPTIONS;
  readonly sensitivityOptions = SENSITIVITY_OPTIONS;

  // Estado del panel de filtros
  public isFilterPanelOpen: boolean = false;

  // Filtros
  public filterKeywords: string = '';
  public filterTextFields: { [key: string]: boolean } = {
    document: true,
    title: true,
    summary: true,
    tags: true
  };

  public selectedCategories: string[] = [];
  public selectedRelevanceCaregiver: string[] = [];
  public selectedTopics: string[] = [];
  public selectedSentiments: string[] = [];
  public selectedSensitivity: number[] = [];

  public filterStartDate: string = '';
  public filterEndDate: string = '';

  // Filtros aplicados que determinan qué documentos se muestran
  public appliedFilters = {
    keywords: '',
    textFields: {
      document: true,
      title: true,
      summary: true,
      tags: true
    } as { [key: string]: boolean },
    categories: [] as string[],
    relevanceCaregiver: [] as string[],
    topics: [] as string[],
    sentiments: [] as string[],
    sensitivity: [] as number[],
    startDate: '',
    endDate: ''
  };

  delete_modal: modal = {
    title:"",
    document: {text:"",id:0,metadata:getDefaultMetadata(),dropdown:false,info:false},
    visible: false
  }
  edit_modal: edit = {
    title:"",
    document: {text:"",id:0,metadata:getDefaultMetadata(),dropdown:false,info:false},
    visible: false,
    newData: ""
  }
  create_modal:boolean = false;
  
  public init(){
    // FORMA 1: Si el ID no cambia estando en la misma página (Snapshot)
    // this.collectionName = this.route.snapshot.paramMap.get('name');
    // FORMA 2: Si el ID puede cambiar sin recargar la página (Observable)
    this.route.paramMap.subscribe(params => {
      this.collectionName = params.get('name') ?? '';
    });
    this.collection.documents = [];
    this.chromadb.getCollection(this.collectionName).subscribe((data: any) => {      
      this.collectionDocs = data.documents.length;
      for(let i in data.documents){
        this.collection.documents.push({
          text: data.documents[i],
          id: data.ids[i],
          metadata: data.metadatas[i],
          dropdown: false,
          info: false
        });
      }      
    });
  }

  constructor(private chromadb:ChromadbService){}
  ngOnInit(): void { 
    this.init()
  }

  public openDropdown(collection: Document): void {
    collection.dropdown = !collection.dropdown;
    for (let c of this.collection.documents) {
      if (c !== collection) {
        c.dropdown = false;
      }
    }
  }

  
  public createModal(): void {
    this.edit_modal.document = {
      text: "",
      id: -1,
      metadata: getDefaultMetadata(),
      dropdown: false,
      info: false
    };
    this.edit_modal.title = "Crear nuevo documento";
    this.edit_modal.visible = true;
    this.create_modal = true;
  }

  public openModal(document: Document,type:string): void {
    document.dropdown = false;
    if(type == "delete"){
      this.delete_modal.document = document;
      this.delete_modal.title = "Quieres eliminar el documento " + document.id + "?";
      this.delete_modal.visible = true;
    }
    if(type == "edit"){
      this.edit_modal.document = {
        ...document,
        metadata: {
          ...getDefaultMetadata(),
          ...(document.metadata || {})
        }
      };
      this.edit_modal.title = "Modificación del documento " + document.id;
      this.edit_modal.newData = document.text;
      this.edit_modal.visible = true;
    }
  }

  public deleteDocument(doc: Document): void {
    console.log("delete")
    this.chromadb.deleteElement(this.collectionName,doc.id+"").subscribe((data: any) => {
      doc.dropdown = false;
      this.delete_modal.visible = false;
      this.init();        
    });
  }

  public onFloatInput(field: 'bias_ai' | 'confidence'): void {
    if (this.edit_modal.document.metadata && this.edit_modal.document.metadata[field] !== undefined) {
      const val = String(this.edit_modal.document.metadata[field]);
      if (val.includes(',')) {
        this.edit_modal.document.metadata[field] = val.replace(/,/g, '.');
      }
    }
  }

  public editDocument(doc: Document): void {
    if (this.create_modal) console.log("create")
    else console.log("edit")

    if (doc.metadata) {
      const parsedBias = parseFloat(String(doc.metadata.bias_ai).replace(',', '.'));
      doc.metadata.bias_ai = isNaN(parsedBias) ? 0 : parsedBias;

      const parsedConfidence = parseFloat(String(doc.metadata.confidence).replace(',', '.'));
      doc.metadata.confidence = isNaN(parsedConfidence) ? 1 : parsedConfidence;

      doc.metadata.counter = Number(doc.metadata.counter) || 0;
      doc.metadata.relevance_ai = Number(doc.metadata.relevance_ai) || 0;
      doc.metadata.sensitivity = Number(doc.metadata.sensitivity) || 0;
      doc.metadata.timestamp = Number(doc.metadata.timestamp) || Date.now();
    }

    this.chromadb.editElement(this.collectionName, doc.id, doc).subscribe((data: any) => {
      doc.dropdown = false;
      this.edit_modal.visible = false;
      this.edit_modal.newData = "";
      this.edit_modal.document = {
        text: "",
        id: 0,
        metadata: getDefaultMetadata(),
        dropdown: false,
        info: false
      };
      if (this.create_modal) this.create_modal = false;
      this.init();
    });
  }

  public closeModal(): void {
    this.edit_modal.visible = false;
    if (this.create_modal) this.create_modal = false;
  }

  public openInfo(document: Document): void {
    document.info = !document.info;
    for (let c of this.collection.documents) {
      if (c.id !== document.id) {
        c.info = false;
      }
    }
  }

  public searchCollection(collectionName: string, query: string, limit: number): void {
    console.log("Navegando a la búsqueda...");
    console.log(query)
    // window.location.href = `/search/${collectionName}?query=${query}&limit=${limit}`;    
    this.router.navigate(['/search', collectionName], {
      queryParams: {
        query: query, // <-- Angular lo convertirá en ?query=valor
        limit: limit  // <-- y esto en &limit=valor
      }
    });    
  }

  public toggleFilterPanel(): void {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
    if (this.isFilterPanelOpen) {
      // Sincronizar el borrador del panel con los filtros actualmente aplicados
      this.syncDraftWithApplied();
    }
  }

  public cancelFilters(): void {
    this.isFilterPanelOpen = false;
    this.syncDraftWithApplied();
  }

  public applyFilters(): void {
    this.appliedFilters = {
      keywords: this.filterKeywords,
      textFields: { ...this.filterTextFields },
      categories: [...this.selectedCategories],
      relevanceCaregiver: [...this.selectedRelevanceCaregiver],
      topics: [...this.selectedTopics],
      sentiments: [...this.selectedSentiments],
      sensitivity: [...this.selectedSensitivity],
      startDate: this.filterStartDate,
      endDate: this.filterEndDate
    };
    this.isFilterPanelOpen = false;
  }

  private syncDraftWithApplied(): void {
    this.filterKeywords = this.appliedFilters.keywords;
    this.filterTextFields = { ...this.appliedFilters.textFields };
    this.selectedCategories = [...this.appliedFilters.categories];
    this.selectedRelevanceCaregiver = [...this.appliedFilters.relevanceCaregiver];
    this.selectedTopics = [...this.appliedFilters.topics];
    this.selectedSentiments = [...this.appliedFilters.sentiments];
    this.selectedSensitivity = [...this.appliedFilters.sensitivity];
    this.filterStartDate = this.appliedFilters.startDate;
    this.filterEndDate = this.appliedFilters.endDate;
  }

  public toggleFilterItem(list: any[], item: any): void {
    const index = list.indexOf(item);
    if (index >= 0) {
      list.splice(index, 1);
    } else {
      list.push(item);
    }
  }

  public isFilterItemSelected(list: any[], item: any): boolean {
    return list.includes(item);
  }

  public toggleTextField(field: string): void {
    this.filterTextFields[field] = !this.filterTextFields[field];
  }

  public resetFilters(): void {
    this.filterKeywords = '';
    this.filterTextFields = {
      document: true,
      title: true,
      summary: true,
      tags: true
    };
    this.selectedCategories = [];
    this.selectedRelevanceCaregiver = [];
    this.selectedTopics = [];
    this.selectedSentiments = [];
    this.selectedSensitivity = [];
    this.filterStartDate = '';
    this.filterEndDate = '';

    this.appliedFilters = {
      keywords: '',
      textFields: {
        document: true,
        title: true,
        summary: true,
        tags: true
      },
      categories: [],
      relevanceCaregiver: [],
      topics: [],
      sentiments: [],
      sensitivity: [],
      startDate: '',
      endDate: ''
    };
  }

  public isFilterActive(): boolean {
    return (
      this.appliedFilters.keywords.trim().length > 0 ||
      this.appliedFilters.categories.length > 0 ||
      this.appliedFilters.relevanceCaregiver.length > 0 ||
      this.appliedFilters.topics.length > 0 ||
      this.appliedFilters.sentiments.length > 0 ||
      this.appliedFilters.sensitivity.length > 0 ||
      !!this.appliedFilters.startDate ||
      !!this.appliedFilters.endDate
    );
  }

  public getActiveFilterCount(): number {
    let count = 0;
    if (this.appliedFilters.keywords.trim().length > 0) count++;
    if (this.appliedFilters.categories.length > 0) count++;
    if (this.appliedFilters.relevanceCaregiver.length > 0) count++;
    if (this.appliedFilters.topics.length > 0) count++;
    if (this.appliedFilters.sentiments.length > 0) count++;
    if (this.appliedFilters.sensitivity.length > 0) count++;
    if (this.appliedFilters.startDate || this.appliedFilters.endDate) count++;
    return count;
  }

  public get filteredDocuments(): Document[] {
    if (!this.collection.documents || this.collection.documents.length === 0) {
      return [];
    }

    const keywords = this.appliedFilters.keywords.trim().toLowerCase();
    const hasKeywords = keywords.length > 0;
    const hasCategoryFilter = this.appliedFilters.categories.length > 0;
    const hasRelevanceFilter = this.appliedFilters.relevanceCaregiver.length > 0;
    const hasTopicFilter = this.appliedFilters.topics.length > 0;
    const hasSentimentFilter = this.appliedFilters.sentiments.length > 0;
    const hasSensitivityFilter = this.appliedFilters.sensitivity.length > 0;
    const hasStartDate = !!this.appliedFilters.startDate;
    const hasEndDate = !!this.appliedFilters.endDate;

    if (
      !hasKeywords &&
      !hasCategoryFilter &&
      !hasRelevanceFilter &&
      !hasTopicFilter &&
      !hasSentimentFilter &&
      !hasSensitivityFilter &&
      !hasStartDate &&
      !hasEndDate
    ) {
      return this.collection.documents;
    }

    // Parse start and end date timestamps in ms
    let startTimestamp: number | null = null;
    let endTimestamp: number | null = null;

    if (hasStartDate) {
      const [y, m, d] = this.appliedFilters.startDate.split('-').map(Number);
      startTimestamp = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
    }

    if (hasEndDate) {
      const [y, m, d] = this.appliedFilters.endDate.split('-').map(Number);
      endTimestamp = new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
    }

    return this.collection.documents.filter(doc => {
      const meta = doc.metadata || {};

      // 1. Category filter
      if (hasCategoryFilter) {
        const docCat = (meta.category || '').toUpperCase();
        if (!this.appliedFilters.categories.includes(docCat)) {
          return false;
        }
      }

      // 2. Relevance Caregiver filter
      if (hasRelevanceFilter) {
        const docRel = (meta.relevance_caregiver || '').toUpperCase();
        if (!this.appliedFilters.relevanceCaregiver.includes(docRel)) {
          return false;
        }
      }

      // 3. Topics filter
      if (hasTopicFilter) {
        const rawTopics = (meta.topics || '').toLowerCase();
        const matched = this.appliedFilters.topics.some(topic => {
          const tLower = topic.toLowerCase();
          return rawTopics.includes(tLower);
        });
        if (!matched) {
          return false;
        }
      }

      // 4. Sentiment filter
      if (hasSentimentFilter) {
        const docSent = (meta.sentiment || '').toUpperCase();
        if (!this.appliedFilters.sentiments.includes(docSent)) {
          return false;
        }
      }

      // 5. Sensitivity filter (values 0 - 4)
      if (hasSensitivityFilter) {
        const docSens = meta.sensitivity !== undefined && meta.sensitivity !== null ? Number(meta.sensitivity) : null;
        if (docSens === null || isNaN(docSens) || !this.appliedFilters.sensitivity.includes(docSens)) {
          return false;
        }
      }

      // 6. Temporal range filter
      if (hasStartDate || hasEndDate) {
        const docTs = this.parseDocTimestamp(meta.timestamp);
        if (docTs === null) {
          return false;
        }
        if (startTimestamp !== null && docTs < startTimestamp) {
          return false;
        }
        if (endTimestamp !== null && docTs > endTimestamp) {
          return false;
        }
      }

      // 6. Text content filter (document, title, summary, tags)
      if (hasKeywords) {
        const targets: string[] = [];
        if (this.appliedFilters.textFields['document'] && doc.text) {
          targets.push(doc.text);
        }
        if (this.appliedFilters.textFields['title'] && meta.title) {
          targets.push(meta.title);
        }
        if (this.appliedFilters.textFields['summary'] && meta.summary) {
          targets.push(meta.summary);
        }
        if (this.appliedFilters.textFields['tags'] && meta.tags) {
          targets.push(meta.tags);
        }

        if (targets.length === 0) {
          return false;
        }

        const combinedText = targets.join(' ').toLowerCase();
        const terms = keywords.split(/\s+/).filter(Boolean);
        const matchesKeywords = terms.every(term => combinedText.includes(term));
        if (!matchesKeywords) {
          return false;
        }
      }

      return true;
    });
  }

  private parseDocTimestamp(ts: any): number | null {
    if (ts === undefined || ts === null || ts === '') return null;
    const num = Number(ts);
    if (!isNaN(num)) {
      return num < 10000000000 ? num * 1000 : num;
    }
    const parsed = Date.parse(String(ts));
    return isNaN(parsed) ? null : parsed;
  }

}
